import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import VueDatePicker from '@vuepic/vue-datepicker'
import FormInputTextField from 'components/FormInputTextField/FormInputTextField.vue'
import FormInputRichText from 'components/FormInputRichText/FormInputRichText.vue'
import phoneValidator from '../../foundation/js/inputValidators/phoneValidator'
import nameValidator from '../../foundation/js/inputValidators/nameValidator'
import textAreaValidator from '../../foundation/js/inputValidators/textAreaValidator'
import { scrollToFirstError } from '../../foundation/js/inputValidators/scrollToFirstError'
import stripHtml from '../../foundation/js/stripHtml'
import TooltipComponent from 'components/TooltipComponent/TooltipComponent.vue'

export default {
    name: 'PersonalFundraising',
    components: {
        VueDatePicker,
        FormInputTextField,
        FormInputRichText,
        TooltipComponent
    },
    props: {
        isMindegave: {
            type: Boolean,
            required: false,
            default: false
        },
        isUpdate: {
            type: Boolean,
            required: false,
            default: false
        },
        personalFundraising: {
            type: Object,
            required: true
        },
        showUnsavedChangesWarning: {
            type: Boolean,
            required: false,
            default: false
        },
        userData: {
            type: Object,
            required: true
        },
        privacyPolicyEncoded: {
            type: String,
            required: false,
            default: ''
        },
        showUserConsent: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {
        const store = useStore()
        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: 'personalFundraising',
                labelKey
            })
        const headline = ref(props.personalFundraising?.Headline ?? '')
        const dateOfbirth = ref(props.personalFundraising?.DateOfBirth)
        const fundraisingTarget = ref(
            props.personalFundraising?.FundraisingTarget ?? ''
        )
        const dateOfdeath = ref(props.personalFundraising?.DateOfDeath)
        const endDate = ref(props.personalFundraising?.EndDate ?? '')
        const fundraiseReason = ref(
            props.personalFundraising?.FundraiseReason ?? ''
        )
        const fundraisePersonDescription = ref(
            props.personalFundraising?.FundraisePersonDescription ?? ''
        )
        const personalStoryImage = ref(
            props.personalFundraising?.personalStoryImages ?? []
        )
        const fundraiseImage = ref(props.personalFundraising?.fundraiseImage)
        const datePickerRefDateOfBirth = ref()
        const datePickerRefDateOfDeath = ref()
        const datePickerRefEndDate = ref()
        const notificationSms = ref(
            props.personalFundraising?.SmsNotification ?? false
        )
        const notificationEmail = ref(
            props.personalFundraising?.EmailNotification ?? false
        )
        const isSubmitting = computed(
            () => store.state.fundraising.isSubmitting
        )

        const firstName = ref(props.userData.firstname)
        const lastName = ref(props.userData.lastname)
        const email = ref(props.userData.email)
        const phone = ref(props.userData.telephone)
        const isReadOnly = ref({
            firstName: !!props.userData.firstname,
            lastName: !!props.userData.lastname,
            email: true,
            phone: !!props.userData.telephone,
        })
        const isFirstNameValid = computed(() => isReadOnly.value.firstName || nameValidator(firstName.value))
        const isLastNameValid = computed(() => isReadOnly.value.lastName || nameValidator(lastName.value))
        const isPhoneValid = computed(() => isReadOnly.value.phone || phoneValidator(phone.value))

        const hasConsent = ref(!!props.personalFundraising?.FundraisePersonDescription)

        const displayError = ref(false)
        const privacyPolicyWrapper = ref(null)

        // Step 1: Personal information
        // Step 2: Fundraising information
        // Only show step 2 if the user is editing the fundraising
        const minStep = ref(props.isUpdate ? 2 : 1)
        const maxStep = ref(2)        
        const step = ref(minStep.value)
        const isFirstStep = computed(() => step.value === minStep.value)
        const isLastStep = computed(() => step.value === maxStep.value)
        
        const showUnsavedChangesWarning = props.showUnsavedChangesWarning;
        const handleBeforeUnload = (e) => {
            if (store.state.fundraising.isEditing && showUnsavedChangesWarning) {
                e.preventDefault();
                e.returnValue = ''; 
            }
        };
        
        if (showUnsavedChangesWarning) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        } else {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        }
        

        const isUploadingFundraiseImage = computed(
            () => store.state.fundraising.isFundraiseImageUploading
        )
        const personalStoryImagesUploading = computed(
            () => store.state.fundraising.personalStoryImagesUploading
        )

        const selectEndDate = () => {
            datePickerRefEndDate.value.selectDate()
        }

        const selectDateOfBirth = () => {
            datePickerRefDateOfBirth.value.selectDate()
        }

        const selectDateOfDeath = () => {
            datePickerRefDateOfDeath.value.selectDate()
        }

        const customPosition = () => ({ top: 10, left: 40 })

        const headlineFilled = computed(() => {
            return headline.value.length > 2 &&
                      headline.value.length <= 75 &&
                      nameValidator(headline.value)
        })

        const dateOfbirthFilled = computed(() => {
            return props.isMindegave ? dateOfbirth.value : true
        })

        const endDateFilled = computed(() => {
            return endDate.value
        })

        const dateOfdeathFilled = computed(() => {
            return props.isMindegave ? dateOfdeath.value : true
        })

        const fundraisingTargetFilled = computed(() => {
            return fundraisingTarget.value >= 100
        })

        const fundraiseReasonFilled = computed(() => {
            const text = stripHtml(fundraiseReason.value)
            return text.length > 2 && text.length <= 2000
        })

        const fundraisePersonDescriptionFilled = computed(() => {

            if (props.isMindegave) {
                return fundraisePersonDescription.value
                    ? fundraisePersonDescription.value.length > 2 &&
                        fundraisePersonDescription.value.length <= 2000 &&
                        textAreaValidator(fundraisePersonDescription.value)
                    : true
            }

            if (hasConsent.value) {
                return !!fundraisePersonDescription.value
            }

            return true
        })

        const personalStoryImageFilled = computed(() => {
            return !!personalStoryImage.value
        })

        const fundraiseImageFilled = computed(() => {
            return fundraiseImage.value
        })

        const pageId = computed(() => store.state.main.page_id)
        const isFundraiseImageUploadError = computed(
            () => store.state.fundraising.isFundraiseImageUploadError
        )
        const isPersonalStoryImageUploadError = computed(
            () => store.state.fundraising.isPersonalStoryImageUploadError
        )

        const submit = function () {
            store.commit('fundraising/set_fundraise_image_upload_error', false)
            store.commit(
                'fundraising/set_personal_story_image_upload_error',
                false
            )

            const payload = {
                content: {
                    pageId: pageId.value,
                    headline: headline.value,
                    dateOfBirth: dateOfbirth.value,
                    dateOfDeath: dateOfdeath.value,
                    fundraisingTarget: parseInt(fundraisingTarget.value),
                    endDate: endDate.value,
                    fundraiseDescription: stripHtml(fundraiseReason.value),
                    fundraisePersonDescription: (props.isMindegave || hasConsent.value)
                        ? fundraisePersonDescription.value || null
                        : null,
                    coverImage: fundraiseImage.value,
                    images: personalStoryImage.value,
                    notificationSms: notificationSms.value,
                    notificationEmail: notificationEmail.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    phone: phone.value
                },
                id: props.personalFundraising.Id
            }
            if (props.isUpdate)
                store.dispatch('fundraising/updatePersonalfundraising', payload)
            else
                store.dispatch('fundraising/createPersonalfundraising', payload)
        }

        const fundraiseImageUploader = async function (e) {
            const imageData = await store.dispatch(
                'fundraising/uploadFundraiseImage',
                {
                    type: 'fundraiseImage',
                    file: e.target.files[0],
                    id: props.personalFundraising.Id
                }
            )
            if (imageData) fundraiseImage.value = imageData
        }

        const personalStoryImageUploader = async function (e) {
            for (var file of e.target.files)
                singlePersonalStoryImageUploader(file)
        }

        const singlePersonalStoryImageUploader = async function (file) {
            const imageData = await store.dispatch(
                'fundraising/uploadFundraiseImage',
                {
                    type: 'personalStoryImage',
                    file: file,
                    id: props.personalFundraising.Id
                }
            )
            if (imageData) personalStoryImage.value.push(imageData)
        }

        const discardUploadedImage = function (imageType, discardFileId) {
            if (imageType === 'fundraiseImage') fundraiseImage.value = null
            if (imageType === 'personalStoryImage')
                personalStoryImage.value = personalStoryImage.value.filter(
                    (image) => image.id !== discardFileId
                )
        }

        const previousButtonPressed = () => {

            if (step.value > minStep.value) {
                step.value -= 1
                store.state.fundraising.personalFundraisingFlowStep = step.value
                scrollTo(0, 0)
                return
            }

            if (history.length <= 1) document.location = defaultRedirectUrl

            const backSteps = document.referrer && document.referrer.toLowerCase().includes('b2clogin.com') ? -2 : -1;
            history.go(backSteps);
        }

        const isCurrentStepValid = () => {
            switch (step.value) {
                case 1:
                    return isFirstNameValid.value && isLastNameValid.value && isPhoneValid.value
                case 2:
                    return (
                        headlineFilled.value &&
                        dateOfbirthFilled.value &&
                        endDateFilled.value &&
                        dateOfdeathFilled.value &&
                        fundraisingTargetFilled.value &&
                        fundraiseReasonFilled.value &&
                        personalStoryImageFilled.value && 
                        fundraisePersonDescriptionFilled.value
                    )
                default:
                    return false
            }
        }

        const nextButtonPressed = () => {

            if (!isCurrentStepValid()) {
                displayError.value = true
                scrollToFirstError('validation-error')
                return
            }
            
            displayError.value = false

            if (step.value === maxStep.value) {
                submit()
                return
            }

            step.value += 1
            store.state.fundraising.personalFundraisingFlowStep = step.value
            scrollTo(0, 0)
        }

        const deleteButtonPressed = function () {
            if (window.confirm(getLabel('deleteConfirmation'))) {
                store.dispatch('fundraising/deletePersonalfundraising', {
                    id: props.personalFundraising.Id,
                    pageId: pageId.value
                })
            }
        }

        const formatFilesize = (fileSize) => {
            var roundedMb = (fileSize / Math.pow(1024, 2)).toFixed(0)
            if (roundedMb >= 1) return roundedMb + ' MB'
            var roundedKb = (fileSize / Math.pow(1024, 1)).toFixed(0)
            return roundedKb + ' KB'
        }

        const disableNextButton = computed(() => {
            return (
                isUploadingFundraiseImage.value ||
                personalStoryImagesUploading.value > 0
            )
        })

        return {
            discardUploadedImage,
            headline,
            dateOfbirth,
            fundraisingTarget,
            dateOfdeath,
            fundraiseReason,
            endDate,
            fundraisePersonDescription,
            personalStoryImage,
            fundraiseImage,
            fundraiseImageUploader,
            disableNextButton,
            headlineFilled,
            dateOfbirthFilled,
            fundraisingTargetFilled,
            dateOfdeathFilled,
            fundraiseImageFilled,
            personalStoryImageFilled,
            fundraiseReasonFilled,
            fundraisePersonDescriptionFilled,
            endDateFilled,
            getLabel,
            selectEndDate,
            datePickerRefEndDate,
            selectDateOfBirth,
            datePickerRefDateOfBirth,
            selectDateOfDeath,
            datePickerRefDateOfDeath,
            customPosition,
            personalStoryImageUploader,
            previousButtonPressed,
            nextButtonPressed,
            deleteButtonPressed,
            formatFilesize,
            isSubmitting,
            isUploadingFundraiseImage,
            personalStoryImagesUploading,
            isFundraiseImageUploadError,
            isPersonalStoryImageUploadError,
            notificationSms,
            notificationEmail,
            step,
            isFirstStep,
            isLastStep,
            firstName,
            lastName,
            email,
            phone,
            isReadOnly,
            isFirstNameValid,
            isLastNameValid,
            isPhoneValid,
            displayError,
            privacyPolicyWrapper,
            hasConsent
        }
    }
}
